/**
 * APP_PAGES — single source of truth for all sidebar pages and their sub-tabs.
 *
 * Used by:
 *   1. MasterData.js — Phase "Linked Work Page" dropdown
 *   2. Any future permission or routing logic
 *
 * To add a new page or sub-tab:
 *   → Add one entry here. It automatically appears in the MasterData dropdown.
 *
 * Fields:
 *   value  — the linkedPageId stored in DB (e.g. "creatives", "creatives.internal")
 *   label  — human-readable name shown in dropdown
 *   icon   — emoji shown in sidebar (optional, for display)
 *   parent — if set, this is a sub-tab of that parent page (used for indentation)
 */
export const APP_PAGES = [
  // ── Content Operations ──────────────────────────────────────────
  { value: 'planning',                  label: 'Monthly Planner',                     icon: '🗓️' },
  { value: 'planning.step1',            label: 'Monthly Planner — Special Days Setup', icon: '🗓️', parent: 'planning' },
  { value: 'planning.step2',            label: 'Monthly Planner — Client Requests',    icon: '🗓️', parent: 'planning' },
  { value: 'planning.step3',            label: 'Monthly Planner — Review Selections',  icon: '🗓️', parent: 'planning' },
  { value: 'planning.step4',            label: 'Monthly Planner — Monthly Planner',    icon: '🗓️', parent: 'planning' },
  { value: 'contentcalendar',           label: 'Content Briefs',                    icon: '📝' },
  { value: 'contentcalendar.scripts',   label: 'Content Briefs — Scripts & Briefs', icon: '📝', parent: 'contentcalendar' },
  { value: 'contentcalendar.internal',  label: 'Content Briefs — Internal Approval',icon: '📝', parent: 'contentcalendar' },
  { value: 'contentcalendar.client',    label: 'Content Briefs — Client Approval',  icon: '📝', parent: 'contentcalendar' },
  { value: 'creatives',                 label: 'Creatives',                           icon: '🎨' },
  { value: 'creatives.pending',         label: 'Creatives — In Progress',             icon: '🎨', parent: 'creatives' },
  { value: 'creatives.internal',        label: 'Creatives — Internal Review',         icon: '🎨', parent: 'creatives' },
  { value: 'creatives.client',          label: 'Creatives — Client Approval',         icon: '🎨', parent: 'creatives' },
  { value: 'creatives.approved',        label: 'Creatives — Approved',                icon: '🎨', parent: 'creatives' },
  { value: 'postcopy',                  label: 'Post Copy',                           icon: '✍️' },
  { value: 'postcopy.draft',            label: 'Post Copy — Draft',                   icon: '✍️', parent: 'postcopy' },
  { value: 'postcopy.internal',         label: 'Post Copy — Internal Approval',       icon: '✍️', parent: 'postcopy' },
  { value: 'postcopy.client',           label: 'Post Copy — Client Approval',         icon: '✍️', parent: 'postcopy' },
  { value: 'postcopy.approved',         label: 'Post Copy — Approved',                icon: '✍️', parent: 'postcopy' },
  { value: 'videoworks',                 label: 'Video Works',                         icon: '📹' },
  { value: 'videoworks.production',      label: 'Video Works — Production',            icon: '📹', parent: 'videoworks' },
  { value: 'videoworks.internal',        label: 'Video Works — Internal Review',       icon: '📹', parent: 'videoworks' },
  { value: 'videoworks.client',          label: 'Video Works — Client Review',         icon: '📹', parent: 'videoworks' },
  { value: 'videoworks.approved',        label: 'Video Works — Approved',              icon: '📹', parent: 'videoworks' },
  { value: 'printmedia',                 label: 'Print Media',                         icon: '🖨️' },
  { value: 'printmedia.design',          label: 'Print Media — Design',                icon: '🖨️', parent: 'printmedia' },
  { value: 'printmedia.internal',        label: 'Print Media — Internal Review',       icon: '🖨️', parent: 'printmedia' },
  { value: 'printmedia.client',          label: 'Print Media — Client Review',         icon: '🖨️', parent: 'printmedia' },
  { value: 'printmedia.approved',        label: 'Print Media — Approved',              icon: '🖨️', parent: 'printmedia' },
  { value: 'scheduling',                label: 'Scheduling & Posting',                icon: '🚀' },
  { value: 'scheduling.ready',          label: 'Scheduling — Ready to Schedule',      icon: '🚀', parent: 'scheduling' },
  { value: 'scheduling.scheduled',      label: 'Scheduling — Scheduled',              icon: '🚀', parent: 'scheduling' },
  { value: 'scheduling.posted',         label: 'Scheduling — Posted / Live',          icon: '🚀', parent: 'scheduling' },
  { value: 'monitoring',                label: 'Monitoring',                          icon: '📡' },
  { value: 'monitoring.active',         label: 'Monitoring — Active',                 icon: '📡', parent: 'monitoring' },
  { value: 'monitoring.closed',         label: 'Monitoring — Closed',                 icon: '📡', parent: 'monitoring' },
  // ── Management ──────────────────────────────────────────────────
  { value: 'workorders',                label: 'Work Orders',                         icon: '📋' },
  { value: 'pipeline',                  label: 'Pipeline',                            icon: '🔄' },
  { value: 'reallocation',              label: 'Work Reallocation',                   icon: '🔀' },
  { value: 'workassignments',           label: 'Work Assignments',                    icon: '👥' },
  { value: 'touchpoints',               label: 'Touchpoints',                         icon: '💬' },
  { value: 'costs',                     label: 'Costs',                               icon: '💰' },
  { value: 'kpi',                       label: 'KPI Tracker',                         icon: '📊' },
  { value: 'performance',               label: 'Performance',                         icon: '📈' },
];

/**
 * Returns APP_PAGES formatted for a <select> dropdown.
 * Sub-tabs are indented with ↳ prefix.
 */
export function getLinkedPageOptions() {
  return APP_PAGES.map(p => ({
    value: p.value,
    label: p.parent ? `↳ ${p.label.replace(/^[^—]+ — /, '')}` : p.label,
  }));
}